import React, { useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { ResponsiveContainer, ComposedChart, Area, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

import { formatFull } from "../GoalProgress/format";
import "../GoalProgress/goalProgress.css";

/* formatFull comes from GoalProgress/format.js, not its data.js: that module's
   chain reaches an indexedDB call at load time (via hooks/kpi.js), which is
   unrelated weight for a component that otherwise has nothing to do with Goal
   Progress's data. The private copy this file used to keep is gone with it. */

/**
 * The same premium chart chrome as Goal Progress's own GoalTrendChart
 * (Recharts ComposedChart: real axes, gradient fill, tooltip, end dot) —
 * reused here for the two derived metrics (Average Daily Revenue,
 * Technicians' Revenue per Hour) only, per Kelvin's ask, rather than
 * touching MetricSparkline/MiniBars, which every other Build Your Own card
 * still renders through. Unlike GoalTrendChart this plots the RAW daily
 * values, not a running total toward a goal — a derived metric has no goal
 * to climb toward, so what's informative here is the day-to-day shape.
 */
const fmtDate = (d) => moment(d, "YYYY-MM-DD").format("MMM D");

const EndDot = ({ cx, cy, payload }) => {
  if (!payload?.isLast) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill="#fff" />
      <circle cx={cx} cy={cy} r={3.2} fill={payload.lineColor} />
    </g>
  );
};
EndDot.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  payload: PropTypes.shape({ isLast: PropTypes.bool, lineColor: PropTypes.string }),
};
EndDot.defaultProps = { cx: 0, cy: 0, payload: null };

const ChartTip = ({ active, payload, label, kind }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="gprog-tip">
      <div className="gprog-tip-t">{fmtDate(label)}</div>
      <div className="gprog-tip-v">{formatFull(payload[0].value, kind)}</div>
    </div>
  );
};
ChartTip.propTypes = {
  active: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  payload: PropTypes.array,
  label: PropTypes.string,
  kind: PropTypes.oneOf(["$", "%", "#"]),
};
ChartTip.defaultProps = { active: false, payload: [], label: "", kind: "#" };

const SERIES_STYLES = ["area", "line", "bar", "spark"];

/**
 * The value axis: round-number gridlines and a top that is the next half-step
 * above the data, so a $317 peak reads $0 / $100 / $200 / $300 / $350 instead of
 * Recharts' $0 / $80 / $160 / $317. The step is the smallest 1 / 2 / 2.5 / 5 / 10
 * × 10ⁿ that needs no more than four gridlines, whatever the metric's size
 * (a 0–1 rate or $400 an hour). The top is a gridline only when it clears the
 * last full step by half a step, so two labels never stack.
 */
export const niceAxis = (max) => {
  const peak = Number(max);
  if (!(peak > 0) || !Number.isFinite(peak)) return { top: 1, ticks: [0, 1] };
  const clean = (n) => Number(n.toPrecision(10));
  const pow = 10 ** Math.floor(Math.log10(peak / 4));
  const step = [1, 2, 2.5, 5, 10, 20].map((m) => clean(m * pow)).find((st) => Math.ceil(clean(peak / st)) <= 4);
  const half = clean(step / 2);
  const top = clean(Math.ceil(clean(peak / half)) * half);
  const ticks = [];
  for (let t = 0; t <= top + half / 1000; t = clean(t + step)) ticks.push(t);
  if (top - ticks[ticks.length - 1] > half / 1000) ticks.push(top);
  return { top, ticks };
};

const DerivedTrendChart = ({ series, kind, color, style, gradientId, height }) => {
  const chartStyle = SERIES_STYLES.includes(style) ? style : "area";
  const isSpark = chartStyle === "spark";

  const data = useMemo(() => {
    /* The series MetricBlock hands down keys each point by `timestamp`
       (see CustomWidget/index.jsx's own daysFor()), not `date` — reading the
       wrong field left every X-axis tick parsing as "Invalid date". */
    const pts = (Array.isArray(series) ? series : []).map((p) => ({ date: p.timestamp ?? p.date, value: Number(p.value) || 0 }));
    if (pts.length) pts[pts.length - 1] = { ...pts[pts.length - 1], isLast: true, lineColor: color };
    return pts;
  }, [series, color]);

  const axis = useMemo(() => niceAxis(Math.max(0, ...data.map((d) => d.value))), [data]);

  if (data.length < 2) return null;

  /* ResponsiveContainer measures its nearest ANCESTOR's resolved height —
     the card wrapper this sits in (CustomWidget's `art` div) never sets one,
     so "100%" here would resolve against nothing and draw a 0px-tall chart.
     A concrete pixel height, same as MetricSparkline gets passed directly,
     sidesteps needing that ancestor at all. */
  return (
    <div style={{ width: "100%", height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          {!isSpark && <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(15,23,42,.08)" />}
          <XAxis
            dataKey="date"
            tickFormatter={fmtDate}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
            minTickGap={24}
            tickMargin={8}
            tick={{ fontSize: 10.5, fill: "#98A2B3", fontWeight: 500 }}
            hide={isSpark}
            padding={{ left: 0, right: 0 }}
          />
          <YAxis
            tickFormatter={(v) => formatFull(v, kind)}
            tickLine={false}
            axisLine={false}
            width={54}
            tickMargin={4}
            tick={{ fontSize: 10.5, fill: "#98A2B3", fontWeight: 500 }}
            domain={[0, axis.top]}
            ticks={axis.ticks}
            hide={isSpark}
          />
          <Tooltip content={<ChartTip kind={kind} />} cursor={{ stroke: color, strokeOpacity: 0.25 }} />
          {chartStyle === "bar" && (
            <Bar dataKey="value" fill={color} radius={[4, 4, 0, 0]} isAnimationActive animationDuration={900} animationEasing="ease-out" />
          )}
          {chartStyle === "line" && (
            <Line
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2.5}
              strokeLinecap="round"
              dot={EndDot}
              activeDot={{ r: 4, fill: color, stroke: "#fff", strokeWidth: 2 }}
              isAnimationActive
              animationDuration={900}
              animationEasing="ease-out"
            />
          )}
          {(chartStyle === "area" || isSpark) && (
            <Area
              type="monotone"
              dataKey="value"
              stroke={color}
              strokeWidth={2.5}
              strokeLinecap="round"
              fill={`url(#${gradientId})`}
              dot={isSpark ? false : EndDot}
              activeDot={{ r: 4, fill: color, stroke: "#fff", strokeWidth: 2 }}
              isAnimationActive
              animationDuration={900}
              animationEasing="ease-out"
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

DerivedTrendChart.propTypes = {
  series: PropTypes.arrayOf(PropTypes.shape({
    date: PropTypes.string,
    value: PropTypes.number,
  })),
  kind: PropTypes.oneOf(["$", "%", "#"]),
  color: PropTypes.string,
  style: PropTypes.oneOf(SERIES_STYLES),
  gradientId: PropTypes.string,
  height: PropTypes.number,
};
DerivedTrendChart.defaultProps = {
  series: [],
  kind: "$",
  color: "#1B598E",
  style: "area",
  gradientId: "derived-trend-fill",
  height: 62,
};

export default DerivedTrendChart;
